import { useEffect, useState } from 'react';

import { api, ApiHiba, type Alapadatok, type BelepettFelhasznalo, type EnValasz } from './api.ts';
import { AlapadatokContext } from './alapadatok-context.ts';
import { FelhasznaloContext } from './felhasznalo-context.ts';
import { UjValtozat } from './reszek/UjValtozat.tsx';
import { Belepes } from './kepernyok/Belepes.tsx';
import { Partnerek } from './kepernyok/Partnerek.tsx';
import { Termekek } from './kepernyok/Termekek.tsx';
import { Szamlak } from './kepernyok/Szamlak.tsx';
import { Bankkivonatok } from './kepernyok/Bankkivonatok.tsx';
import { Adatbetoltes } from './kepernyok/Adatbetoltes.tsx';
import { Adatminoseg } from './kepernyok/Adatminoseg.tsx';
import { Attekintes } from './kepernyok/Attekintes.tsx';
import { ElmaradoPartnerek } from './kepernyok/ElmaradoPartnerek.tsx';
import { Felszolitasok } from './kepernyok/Felszolitasok.tsx';
import { GlobalisKereso } from './reszek/GlobalisKereso.tsx';
import { Szerzodesek } from './kepernyok/Szerzodesek.tsx';
import { SoforokEsAutok } from './kepernyok/SoforokEsAutok.tsx';
import { AllandoCimek } from './kepernyok/AllandoCimek.tsx';
import { Jaratszervezes } from './kepernyok/Jaratszervezes.tsx';
import { Nyomtatas } from './kepernyok/Nyomtatas.tsx';
import { Ellenorzes } from './kepernyok/Ellenorzes.tsx';
import { NapiBontas } from './kepernyok/NapiBontas.tsx';
import { Partnerkarton } from './kepernyok/Partnerkarton.tsx';
import { Autokeszlet } from './kepernyok/Autokeszlet.tsx';
import { HaviZaras } from './kepernyok/HaviZaras.tsx';
import { Jaratlapok } from './kepernyok/Jaratlapok.tsx';
import { Hamarosan } from './kepernyok/Hamarosan.tsx';
import { Naplo } from './kepernyok/Naplo.tsx';
import { Felhasznalok } from './kepernyok/Felhasznalok.tsx';
import { Cegadatok } from './kepernyok/Cegadatok.tsx';
import { Sablonok } from './kepernyok/Sablonok.tsx';
import { EmailBeallitas } from './kepernyok/EmailBeallitas.tsx';
import { Szallitolevel } from './kepernyok/Szallitolevel.tsx';
import { SoforJaratlap, type SzlevKezdo } from './kepernyok/SoforJaratlap.tsx';
import { MobilContext } from './mobil-context.ts';

/**
 * A program váza: bal oldalt az állandó menüsáv, jobbra a kiválasztott képernyő.
 *
 * A menüpontok sorrendje és neve a megbízás 6. pontját követi. Amelyik képernyő
 * még nem készült el, az szürkén, kikapcsolva jelenik meg — így látszik, hol
 * tart a munka, és nem lehet félkész felületbe belefutni.
 */

interface Menupont {
  kulcs: string;
  cimke: string;
  kesz: boolean;
  /** Melyik 2.0-s blokkban készül el, ha még nincs kész. */
  blokk: number;
  /**
   * Melyik jogosultsági kulcshoz tartozik a szerveren. (3.0 / 1.)
   * Ha a felhasználó szintje 0, a menüpont **meg sem jelenik**.
   */
  jog: string;
  /** Ha művelet-jog kell hozzá (nem menüpont-szint), ez a kulcsa. */
  muvelet?: string;
}

interface Menucsoport {
  kulcs: string;
  cimke: string;
  pontok: Menupont[];
}

/**
 * A menü négy csoportra bomlik. (Megbízás 2.0 / 7.)
 *
 * Az 1.0-ban nyolc pont volt egymás alatt; a 2.0-ban ez huszonvalahányra nő,
 * ezért kellett a csoportosítás. Ami még nem készült el, az szürkén látszik —
 * így látni, hol tart a munka, és nem lehet félkész felületbe belefutni.
 */
const CSOPORTOK: Menucsoport[] = [
  {
    kulcs: 'napi',
    cimke: 'Napi munka',
    pontok: [
      { kulcs: 'attekintes', cimke: 'Áttekintés', kesz: true, blokk: 9, jog: 'atte' },
      { kulcs: 'jarat', cimke: 'Járatszervezés', kesz: true, blokk: 4, jog: 'jarat' },
      { kulcs: 'nyomtatas', cimke: 'Nyomtatási kép', kesz: true, blokk: 4, jog: 'preview' },
      { kulcs: 'ellenorzes', cimke: 'Ellenőrzés', kesz: true, blokk: 4, jog: 'ell' },
      { kulcs: 'sofjar', cimke: 'Járatlap (sofőr)', kesz: true, blokk: 0, jog: 'sofjar' },
      { kulcs: 'szallitolevel', cimke: 'Szállítólevél', kesz: true, blokk: 0, jog: 'szlev' },
    ],
  },
  {
    kulcs: 'kimutatas',
    cimke: 'Kimutatások',
    pontok: [
      { kulcs: 'bontas', cimke: 'Napi bontás', kesz: true, blokk: 0, jog: 'bontas' },
      { kulcs: 'keszlet', cimke: 'Autókészlet', kesz: true, blokk: 0, jog: 'keszlet' },
      { kulcs: 'archivum', cimke: 'Járatlapok', kesz: true, blokk: 0, jog: 'arch' },
      { kulcs: 'zaras', cimke: 'Havi zárás', kesz: true, blokk: 0, jog: 'zaras' },
      { kulcs: 'elmarado', cimke: 'Elmaradó partnerek', kesz: true, blokk: 9, jog: 'elmar' },
    ],
  },
  {
    kulcs: 'penzugy',
    cimke: 'Pénzügy',
    pontok: [
      { kulcs: 'szamlak', cimke: 'Számlák', kesz: true, blokk: 5, jog: 'szamla' },
      { kulcs: 'bank', cimke: 'Bankkivonatok', kesz: true, blokk: 6, jog: 'bank' },
      { kulcs: 'felszolitas', cimke: 'Felszólítások', kesz: true, blokk: 8, jog: 'felsz' },
    ],
  },
  {
    kulcs: 'torzs',
    cimke: 'Törzsadatok',
    pontok: [
      { kulcs: 'partnerek', cimke: 'Partnerek', kesz: true, blokk: 0, jog: 'torzs' },
      { kulcs: 'termekek', cimke: 'Termékek', kesz: true, blokk: 2, jog: 'termek' },
      { kulcs: 'jarmuvek', cimke: 'Sofőrök és autók', kesz: true, blokk: 2, jog: 'jarmu' },
      { kulcs: 'szerzodesek', cimke: 'Szerződések', kesz: true, blokk: 7, jog: 'szerz' },
      { kulcs: 'allando', cimke: 'Állandó címek', kesz: true, blokk: 0, jog: 'allando' },
      { kulcs: 'minoseg', cimke: 'Adatminőség', kesz: true, blokk: 9, jog: 'minoseg' },
      // Az adatbetöltés nem menüpont a 3.0-ban: a „Betöltés fájlból" művelet
      // engedélyéhez kötött.
      { kulcs: 'betoltes', cimke: 'Adatbetöltés', kesz: true, blokk: 10, jog: 'minoseg', muvelet: 'import' },
    ],
  },
  {
    kulcs: 'beallitas',
    cimke: 'Beállítások',
    pontok: [
      { kulcs: 'cegadat', cimke: 'Cégadatok', kesz: true, blokk: 0, jog: 'cegadat' },
      { kulcs: 'felhasznalok', cimke: 'Felhasználók', kesz: true, blokk: 0, jog: 'felh' },
      { kulcs: 'szerepkorok', cimke: 'Szerepkörök', kesz: true, blokk: 0, jog: 'szerep' },
      // A sablonok is itt élnek: az okmány szövege és a kísérő levél együvé tartozik.
      { kulcs: 'email', cimke: 'E-mail és sablonok', kesz: true, blokk: 0, jog: 'email' },
      { kulcs: 'naplo', cimke: 'Napló', kesz: true, blokk: 0, jog: 'naplo' },
    ],
  },
];

const MENU: Menupont[] = CSOPORTOK.flatMap((cs) => cs.pontok);

/**
 * Menüpontonként egy jel a csempés menühöz. (3.0 / 8.)
 *
 * Szándékosan betűjelek, nem képek: nem kell hozzá külön fájl, minden
 * telefonon ugyanúgy néz ki, és a menü így is első pillantásra átlátható.
 */
const IKON: Record<string, string> = {
  attekintes: '◉',
  jarat: '▤',
  nyomtatas: '⎙',
  ellenorzes: '✓',
  sofjar: '◱',
  szallitolevel: '✎',
  bontas: '▦',
  keszlet: '⛁',
  zaras: 'Σ',
  archivum: '🗀',
  elmarado: '↓',
  szamlak: '₣',
  bank: '⇄',
  felszolitas: '✉',
  partnerek: '☰',
  termekek: '◈',
  jarmuvek: '⛟',
  szerzodesek: '§',
  allando: '★',
  minoseg: '⚠',
  betoltes: '⇪',
  cegadat: '⌂',
  sablon: '❏',
  email: '✉',
  felhasznalok: '☺',
  szerepkorok: '⚿',
  naplo: '⧗',
};

/** Ebben jegyezzük meg, ha valaki kézzel váltott nézetet. */
const NEZET_KULCS = 'mhbcrm-nezet';
/** E képpontszám alatt magától telefonos nézet indul. (3.0 / 8.) */
const MOBIL_HATAR = 760;

export function Alkalmazas() {
  const [alapadatok, setAlapadatok] = useState<Alapadatok | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  /**
   * A belépett felhasználó. Amíg `betoltve` hamis, még nem tudjuk, be
   * vagyunk-e jelentkezve — addig se a belépőt, se a programot nem mutatjuk,
   * különben minden frissítéskor felvillanna a bejelentkező képernyő.
   */
  const [felhasznalo, setFelhasznalo] = useState<BelepettFelhasznalo | null>(null);
  const [betoltve, setBetoltve] = useState(false);
  // A nap az Áttekintéssel kezdődik: onnan látszik, mi vár tennivalóra.
  const [aktiv, setAktiv] = useState('attekintes');
  /** Melyik menücsoport van összecsukva. Alapból mind nyitva. */
  const [csukott, setCsukott] = useState<Record<string, boolean>>({});
  /**
   * Ha egyik képernyőről a másikra ugrunk egy adott nappal (például a
   * Járatlapok listájáról), ez viszi át, melyik napot kell megnyitni.
   */
  const [atvittNap, setAtvittNap] = useState<string | null>(null);
  /**
   * Az Áttekintés teendői és a globális kereső nem csak képernyőre ugranak,
   * hanem szűrőt vagy keresőszót is visznek magukkal — különben a
   * felhasználónak újra meg kellene keresnie ugyanazt.
   */
  const [atvittSzuro, setAtvittSzuro] = useState<string | undefined>(undefined);
  const [atvittKereses, setAtvittKereses] = useState<string | undefined>(undefined);
  /** A képernyő újraépítéséhez, hogy a kezdőérték tényleg érvényre jusson. */
  const [ugrasSorszam, setUgrasSorszam] = useState(0);
  /**
   * A sofőri járatlapról a szállítólevél űrlapja a partnerrel és a beírt
   * mennyiségekkel nyílik meg — ez viszi át. (3.0 / 6.)
   */
  const [szlevKezdo, setSzlevKezdo] = useState<SzlevKezdo | null>(null);
  /**
   * Telefonos nézet. Keskeny képernyőn magától bekapcsol, de kézzel is
   * váltható mindkét irányba — és a választást megjegyezzük. (3.0 / 8.)
   */
  const [mobil, setMobil] = useState(() => {
    try {
      const mentett = localStorage.getItem(NEZET_KULCS);
      if (mentett === 'mobil') return true;
      if (mentett === 'asztal') return false;
    } catch {
      /* ha a böngésző nem enged tárolni, a képernyő szélessége dönt */
    }
    return window.innerWidth < MOBIL_HATAR;
  });
  /** Nyitva van-e a teljes képernyős csempés menü. */
  const [menuNyit, setMenuNyit] = useState(false);

  const ugras = (
    kepernyo: string,
    opciok: { datum?: string; szuro?: string; q?: string } = {},
  ) => {
    setAtvittNap(opciok.datum ?? null);
    setAtvittSzuro(opciok.szuro);
    setAtvittKereses(opciok.q);
    setUgrasSorszam((n) => n + 1);
    setKarton(null);
    setSzlevKezdo(null);
    setAktiv(kepernyo);
  };
  /**
   * A partnerkarton nem menüpont: a Napi bontásból és a Törzsadatokból nyílik,
   * és a vissza gomb oda visz, ahonnan jöttünk.
   */
  const [karton, setKarton] = useState<{ etterem_id: number; honnan: string } | null>(null);
  /** Gyakorló példány — a tetején figyelmeztető sáv. (2026-09-29) */
  const [gyakorlo, setGyakorlo] = useState(false);

  /** Ki van bejelentkezve — ezzel indul a program. (3.0 / 1.) */
  useEffect(() => {
    api
      .get<EnValasz>('/api/en')
      .then((v) => {
        setFelhasznalo(v.felhasznalo);
        setGyakorlo(!!v.gyakorlo);
      })
      .catch((h: Error) => {
        // A 401 nem hiba: egyszerűen még nem léptünk be.
        if (!(h instanceof ApiHiba) || h.statusz !== 401) setHiba(h.message);
      })
      .finally(() => setBetoltve(true));
  }, []);

  /** Az alapadatok csak belépés után kellenek — enélkül úgysem adná ki. */
  useEffect(() => {
    if (!felhasznalo) {
      setAlapadatok(null);
      return;
    }
    api
      .get<Alapadatok>('/api/alapadatok')
      .then(setAlapadatok)
      .catch((h: Error) => setHiba(h.message));
  }, [felhasznalo]);

  /** A megjelenést a body két osztálya kapcsolja — ugyanúgy, mint a demóban. */
  useEffect(() => {
    document.body.classList.toggle('mobil', mobil);
    document.body.classList.toggle('mobnyit', mobil && menuNyit);
  }, [mobil, menuNyit]);

  /**
   * Ha valaki keskeny ablakban nyitja meg (telefon, fektetett tábla),
   * magától átvált. Aki viszont kézzel választott nézetet, annál nem
   * kapcsolgatunk — se elforgatáskor, se ablakátméretezéskor.
   */
  useEffect(() => {
    const figyelo = () => {
      try {
        if (localStorage.getItem(NEZET_KULCS)) return;
      } catch {
        /* ha nincs tárolás, a szélesség dönt */
      }
      if (window.innerWidth < MOBIL_HATAR) setMobil(true);
    };
    window.addEventListener('resize', figyelo);
    return () => window.removeEventListener('resize', figyelo);
  }, []);

  function nezetValtas(be: boolean) {
    setMobil(be);
    setMenuNyit(false);
    try {
      localStorage.setItem(NEZET_KULCS, be ? 'mobil' : 'asztal');
    } catch {
      /* a tárolás hiánya nem akadály */
    }
  }

  async function kilep() {
    try {
      await api.post('/api/kilepes');
    } finally {
      setFelhasznalo(null);
      setAktiv('attekintes');
    }
  }

  if (hiba) {
    return (
      <div className="indulohiba">
        <h1>A program nem éri el a háttérprogramot</h1>
        <p>{hiba}</p>
        <p className="halvany">
          Ellenőrizd, hogy fut-e az a fekete ablak, amit a <code>Jaratlap.bat</code> nyitott.
          Ha bezárult, indítsd el újra.
        </p>
        <button className="btn pri" onClick={() => location.reload()}>
          Újra
        </button>
      </div>
    );
  }

  if (!betoltve) {
    return <div className="indulohiba halvany">Betöltés…</div>;
  }

  if (!felhasznalo) {
    /* A sáv a belépő képernyőn is kell: ott dől el, ki hova lép be. */
    return (
      <>
        {gyakorlo && <GyakorloSav />}
        <UjValtozat />
        <Belepes onBelepett={(v) => setFelhasznalo(v.felhasznalo)} />
      </>
    );
  }

  if (!alapadatok) {
    return <div className="indulohiba halvany">Betöltés…</div>;
  }

  /** Amihez nincs joga, azt meg sem mutatjuk. (3.0 / 1.) */
  const lathato = (m: Menupont) =>
    (felhasznalo.jogok[m.jog] ?? 0) > 0 &&
    (!m.muvelet || felhasznalo.admin || (felhasznalo.jogok[m.muvelet] ?? 0) > 0);
  const lathatoCsoportok = CSOPORTOK.map((cs) => ({
    ...cs,
    pontok: cs.pontok.filter(lathato),
  })).filter((cs) => cs.pontok.length > 0);

  const aktivPont = MENU.find((m) => m.kulcs === aktiv);
  /**
   * Ha a felhasználó olyan képernyőn állna, amit nem láthat (mert épp
   * elvették a jogát), az első elérhetőre lépünk.
   */
  const megnyithato = aktivPont ? lathato(aktivPont) : false;
  const elsoElerheto = lathatoCsoportok[0]?.pontok[0]?.kulcs;
  if (!megnyithato && elsoElerheto && aktiv !== elsoElerheto) {
    setAktiv(elsoElerheto);
  }

  return (
    <MobilContext.Provider value={mobil}>
    <FelhasznaloContext.Provider value={felhasznalo}>
    <AlapadatokContext.Provider value={alapadatok}>
      {/*
        * Gyakorló példány: külön adatbázis, külön cím. Amit itt csinálnak,
        * az nem éles — ezt nem lehet elégszer kiírni. (2026-09-29)
        */}
      {gyakorlo && <GyakorloSav />}
      <UjValtozat />
      <nav className="side2">
        {/* A rendszer neve a 3.0-tól MHB CRM. (CLAUDE-3.0.md bevezető) */}
        <div className="brand2">MHB CRM</div>
        <GlobalisKereso
          onTalalat={(u) => {
            if (u.etterem_id !== undefined) {
              setKarton({ etterem_id: u.etterem_id, honnan: 'a kereséshez' });
              return;
            }
            ugras(u.kepernyo, { q: u.q });
          }}
        />
        {lathatoCsoportok.map((cs, i) => (
          <div key={cs.kulcs} className={`mcsop${i === 0 ? ' elso' : ''}`}>
            <button
              className={`ghd${csukott[cs.kulcs] ? ' closed' : ''}`}
              onClick={() => setCsukott((e) => ({ ...e, [cs.kulcs]: !e[cs.kulcs] }))}
            >
              {cs.cimke}
              <span className="chev">▾</span>
            </button>
            <div className={`gbody${csukott[cs.kulcs] ? ' closed' : ''}`}>
              {cs.pontok.map((m) => (
                <button
                  key={m.kulcs}
                  className={`tab${aktiv === m.kulcs ? ' on' : ''}`}
                  disabled={!m.kesz}
                  title={m.kesz ? undefined : `A 2.0 ${m.blokk}. blokkjában készül el`}
                  onClick={() => {
                    // Menüből váltva a képernyő a saját alapértelmezett napjával
                    // induljon, ne az előző ugrásból ittragadttal.
                    setAtvittNap(null);
                    setKarton(null);
                    setSzlevKezdo(null);
                    setAktiv(m.kulcs);
                  }}
                >
                  {m.cimke}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div className="nfoot">
          <div className="nfelh">
            {felhasznalo.nev}
            <span>
              {felhasznalo.szerepkor_nev}
              {felhasznalo.sofor_nev ? ` · ${felhasznalo.sofor_nev}` : ''}
            </span>
          </div>
          <button className="kilep" onClick={() => nezetValtas(true)}>
            Mobil nézet
          </button>
          <button className="kilep" onClick={() => void kilep()}>
            Kilépés
          </button>
        </div>
      </nav>

      {/* Telefonon a bal sáv helyett egy felső sáv és egy csempés menü. (3.0 / 8.) */}
      <div id="mobfej">
        <button id="mob-menu" aria-label="Menü" onClick={() => setMenuNyit(!menuNyit)}>
          ☰
        </button>
        <span id="mob-cim">{karton ? 'Partnerkarton' : (aktivPont?.cimke ?? '')}</span>
        <span id="mob-felh">{felhasznalo.sofor_nev ?? felhasznalo.nev}</span>
      </div>

      <div id="mobmenu">
        {menuNyit && (
          <>
            <div className="mmfej">
              <span>Menü</span>
              <button onClick={() => setMenuNyit(false)} aria-label="Bezárás">
                ✕
              </button>
            </div>
            <div className="mmtest">
              {lathatoCsoportok.map((cs) => (
                <div key={cs.kulcs}>
                  <div className="mmcs">{cs.cimke}</div>
                  <div className="mmgrid">
                    {cs.pontok.map((m) => (
                      <button
                        key={m.kulcs}
                        className="mmcs2"
                        disabled={!m.kesz}
                        onClick={() => {
                          setAtvittNap(null);
                          setKarton(null);
                          setSzlevKezdo(null);
                          setAktiv(m.kulcs);
                          setMenuNyit(false);
                        }}
                      >
                        <span className="mmi">{IKON[m.kulcs] ?? '•'}</span>
                        {m.cimke}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mmlab">
              <div className="mmfelh">
                {felhasznalo.nev} · {felhasznalo.szerepkor_nev}
              </div>
              <button className="btn sm" onClick={() => nezetValtas(false)}>
                Asztali nézet
              </button>
              <button
                className="btn sm"
                onClick={() => {
                  setMenuNyit(false);
                  void kilep();
                }}
              >
                Kilépés
              </button>
            </div>
          </>
        )}
      </div>

      <div className="wrap">
        {karton ? (
          <Partnerkarton
            etterem_id={karton.etterem_id}
            honnan={karton.honnan}
            onVissza={() => setKarton(null)}
          />
        ) : aktiv === 'attekintes' ? (
          <Attekintes onUgras={ugras} />
        ) : aktiv === 'betoltes' ? (
          <Adatbetoltes onUgras={ugras} />
        ) : aktiv === 'minoseg' ? (
          <Adatminoseg onUgras={ugras} />
        ) : aktiv === 'elmarado' ? (
          <ElmaradoPartnerek
            onKarton={(etterem_id) =>
              setKarton({ etterem_id, honnan: 'az elmaradó partnerekhez' })
            }
          />
        ) : aktiv === 'jarat' ? (
          <Jaratszervezes
            kezdoNap={atvittNap}
            onNyomtatas={(d) => ugras('nyomtatas', { datum: d })}
          />
        ) : aktiv === 'nyomtatas' ? (
          <Nyomtatas kezdoNap={atvittNap} />
        ) : aktiv === 'ellenorzes' ? (
          <Ellenorzes kezdoNap={atvittNap} />
        ) : aktiv === 'zaras' ? (
          <HaviZaras />
        ) : aktiv === 'keszlet' ? (
          <Autokeszlet />
        ) : aktiv === 'archivum' ? (
          <Jaratlapok
            onSzerkesztes={(d) => ugras('jarat', { datum: d })}
            onEllenorzes={(d) => ugras('ellenorzes', { datum: d })}
            onNyomtatas={(d) => ugras('nyomtatas', { datum: d })}
          />
        ) : aktiv === 'bontas' ? (
          <NapiBontas
            onKarton={(etterem_id) => setKarton({ etterem_id, honnan: 'a napi bontáshoz' })}
          />
        ) : aktiv === 'partnerek' ? (
          <Partnerek
            key={ugrasSorszam}
            onKarton={(etterem_id) => setKarton({ etterem_id, honnan: 'a partnerekhez' })}
            kezdoSzuro={atvittSzuro}
            kezdoKereses={atvittKereses}
          />
        ) : aktiv === 'szamlak' ? (
          <Szamlak key={ugrasSorszam} kezdoSzuro={atvittSzuro} kezdoKereses={atvittKereses} />
        ) : aktiv === 'bank' ? (
          <Bankkivonatok />
        ) : aktiv === 'felszolitas' ? (
          <Felszolitasok key={ugrasSorszam} kezdoSzuro={atvittSzuro} />
        ) : aktiv === 'szerzodesek' ? (
          <Szerzodesek
            key={ugrasSorszam}
            kezdoSzuro={atvittSzuro}
            onKarton={(etterem_id) => setKarton({ etterem_id, honnan: 'a szerződésekhez' })}
          />
        ) : aktiv === 'termekek' ? (
          <Termekek />
        ) : aktiv === 'jarmuvek' ? (
          <SoforokEsAutok />
        ) : aktiv === 'allando' ? (
          <AllandoCimek />
        ) : aktiv === 'felhasznalok' || aktiv === 'szerepkorok' ? (
          <Felhasznalok key={aktiv} kezdoFul={aktiv} />
        ) : aktiv === 'naplo' ? (
          <Naplo />
        ) : aktiv === 'sofjar' ? (
          <SoforJaratlap
            onSzallitolevel={(k) => {
              setSzlevKezdo(k);
              setUgrasSorszam((n) => n + 1);
              setAktiv('szallitolevel');
            }}
          />
        ) : aktiv === 'szallitolevel' ? (
          <Szallitolevel key={ugrasSorszam} kezdo={szlevKezdo} />
        ) : aktiv === 'cegadat' ? (
          <Cegadatok />
        ) : aktiv === 'sablon' ? (
          <Sablonok />
        ) : aktiv === 'email' ? (
          <EmailBeallitas />
        ) : (
          <Hamarosan cimke={aktivPont?.cimke ?? ''} blokk={aktivPont?.blokk ?? 0} />
        )}
      </div>
    </AlapadatokContext.Provider>
    </FelhasznaloContext.Provider>
    </MobilContext.Provider>
  );
}

/**
 * Gyakorló példány sávja. (2026-09-29)
 *
 * Külön adatbázis, külön cím — de ugyanaz a felület, ezért nem lehet
 * elégszer kiírni, hogy amit itt csinálnak, az nem éles.
 */
function GyakorloSav() {
  return (
    <div className="gyaksav">
      GYAKORLÓ — amit itt csinálsz, <b>nem éles</b>. A bizonylatok és a levelek nem mennek ki
      partnerhez.
    </div>
  );
}
